import orderBy from 'lodash/orderBy'
import { getOrientation } from '@mixtiles/web-backend-shared'
import pickBy from 'lodash/pickBy'

// If possible, try to keep the photo in the same position and return a mapping of assigned photoId to photo
function matchExistingPhotoPositions({ collagePhotos, photoPositions }) {
  const existingPhotoPositions = {}
  const collagePhotosToReposition = []
  collagePhotos.forEach((photo) => {
    const { photoId } = photo
    // If there is a defined photoId and the new layout also has this position, use it
    if (photoId && Object.keys(photoPositions).includes(photoId)) {
      existingPhotoPositions[photoId] = photo
    } else {
      // Otherwise, add to list of photos that need reassignment
      collagePhotosToReposition.push({ ...photo, photoId: null })
    }
  })
  return { existingPhotoPositions, collagePhotosToReposition }
}

/**
 * Finds the best matching photo for each position in the collage.
 * First try to keep the photo in the same position if possible.
 * Iterates the positions from largest to smallest, and finds the largest photo that matches the orientation of the position.
 * If there are none, it will find the largest photo overall.
 *
 * Returns a list of [photo, positionId] pairs.
 */
export function findPositionMatching({ collagePhotos, photoPositions }) {
  const { existingPhotoPositions, collagePhotosToReposition } =
    matchExistingPhotoPositions({ collagePhotos, photoPositions })

  const unassignedPhotoPositions = pickBy(
    photoPositions,
    (_position, positionId) => !existingPhotoPositions[positionId]
  )
  const sortedPositions = sortPositionsByDescendingSize(
    unassignedPhotoPositions
  )

  let unmatchedPhotoIndices = Object.keys(unassignedPhotoPositions)
  const newMatching = sortedPositions.map(([positionId, position], index) => {
    // If all photos have been assigned, set an empty photo
    if (index >= collagePhotosToReposition.length) {
      return [positionId, null]
    }
    // Find best fit photo that was not already assigned
    const remainingPhotos = collagePhotosToReposition.slice(index)
    const mostFitPhoto = orderBy(
      remainingPhotos,
      [
        (i) =>
          remainingPhotos[i]?.uid &&
          getOrientation(remainingPhotos[i]) === getOrientation(position)
            ? 1
            : 0, // Match by orientation
        (i) => (remainingPhotos[i]?.uid ? getSize(remainingPhotos[i]) : 0), // Max size
      ],
      ['desc', 'desc']
    )[0]
    unmatchedPhotoIndices = unmatchedPhotoIndices.filter(
      (i) => i !== positionId
    )
    return [positionId, mostFitPhoto]
  })

  return { ...existingPhotoPositions, ...Object.fromEntries(newMatching) }
}

// Sort positions by size, from largest to smallest. If two positions have the
// same size, sort from top-left to bottom-right
function sortPositionsByDescendingSize(positions) {
  return Object.entries(positions).sort(([, p1], [, p2]) => {
    if (getSize(p2) !== getSize(p1)) {
      return getSize(p2) - getSize(p1)
    } else {
      // Sort from top-left to bottom-right
      if (p1.y !== p2.y) {
        return p1.y - p2.y
      } else {
        return p1.x - p2.x
      }
    }
  })
}

// Return the size (width * height). Before multiplying, round width and height to the nearest
// 10 because layouts aren't accurate (so both of 751.32 and 752.78 will be rounded to 750)
function getSize({ width, height }) {
  const _normalizeSize = (size) => Math.floor(size - (size % 10))
  return _normalizeSize(width) * _normalizeSize(height)
}
